import * as dagre from "@dagrejs/dagre";
import type { Node as XYNode, Edge as XYEdge } from "@xyflow/react";
import { WorkflowNode, NodeId } from "../types";
import { getNodeOptions, getNodeText } from "../WorkflowNodeHelpers";
import { AIConfiguration } from "../../Bots/types";
import { WorkflowListItem } from "../types";

export const NODE_W = 248;
export const NODE_H = 86;
const END_W = 32;
const END_H = 32;

export type FlowWorkflowNodeData = {
  wfNode: WorkflowNode;
  nodeText: string;
};

function terminalEndId(parentId: NodeId, optionIndex?: number) {
  return optionIndex != null
    ? `__end__-${parentId}-${optionIndex}`
    : `__end__-${parentId}`;
}

export function buildFlow(
  nodes: Record<NodeId, WorkflowNode>,
  aiConfigurations: AIConfiguration[],
  getDisplayNameForUser: (id: number) => string,
  getTeamNameById: (id: number) => string,
  workflows: WorkflowListItem[]
): { flowNodes: XYNode[]; flowEdges: XYEdge[] } {
  const flowNodes: XYNode[] = [];
  const flowEdges: XYEdge[] = [];
  const addedEndNodes = new Set<string>();

  function ensureEndNode(endId: string) {
    if (!addedEndNodes.has(endId)) {
      addedEndNodes.add(endId);
      flowNodes.push({ id: endId, type: "workflowEnd", position: { x: 0, y: 0 }, data: {} });
    }
  }

  for (const node of Object.values(nodes)) {
    const nodeText = getNodeText(
      node,
      nodes,
      aiConfigurations,
      getDisplayNameForUser,
      getTeamNameById,
      workflows
    );
    flowNodes.push({
      id: String(node.id),
      type: "workflowNode",
      position: { x: 0, y: 0 },
      data: { wfNode: node, nodeText: nodeText ?? "" } satisfies FlowWorkflowNodeData,
    });

    const options = getNodeOptions(node);
    if (options && options.length > 0) {
      options.forEach((opt, i) => {
        const targetId =
          opt.childNodeId != null && nodes[opt.childNodeId]
            ? String(opt.childNodeId)
            : null;
        const effectiveTarget = targetId ?? (() => {
          const eid = terminalEndId(node.id, i);
          ensureEndNode(eid);
          return eid;
        })();
        flowEdges.push({
          id: `e-${node.id}-${i}-${effectiveTarget}`,
          source: String(node.id),
          target: effectiveTarget,
          label: opt.label || (options.length > 1 ? `Branch ${i + 1}` : undefined),
          type: "workflowEdge",
          data: {
            parentNodeId: node.id,
            parentOptionIndex: i,
            childNodeId: opt.childNodeId,
          },
        });
      });
      continue;
    }

    // jumpTo is a visual terminal — no forward edge
    if (node.actionType === "jumpTo") continue;

    if (node.child != null && nodes[node.child]) {
      flowEdges.push({
        id: `e-${node.id}-${node.child}`,
        source: String(node.id),
        target: String(node.child),
        type: "workflowEdge",
        data: { parentNodeId: node.id, childNodeId: node.child },
      });
    } else {
      const eid = terminalEndId(node.id);
      ensureEndNode(eid);
      flowEdges.push({
        id: `e-${node.id}-end`,
        source: String(node.id),
        target: eid,
        type: "workflowEdge",
        data: { parentNodeId: node.id, childNodeId: undefined },
      });
    }
  }

  // dagre layout
  const g = new dagre.graphlib.Graph({ multigraph: true });
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 160, ranksep: 100 });

  flowNodes.forEach((n) => {
    const isEnd = n.type === "workflowEnd";
    g.setNode(n.id, { width: isEnd ? END_W : NODE_W, height: isEnd ? END_H : NODE_H });
  });
  flowEdges.forEach((e) => g.setEdge(e.source, e.target, {}, e.id));

  dagre.layout(g);

  return {
    flowNodes: flowNodes.map((n) => {
      const pos = g.node(n.id);
      if (!pos) return n;
      const w = n.type === "workflowEnd" ? END_W : NODE_W;
      const h = n.type === "workflowEnd" ? END_H : NODE_H;
      return { ...n, position: { x: pos.x - w / 2, y: pos.y - h / 2 } };
    }),
    flowEdges,
  };
}
